/**
 * Shared team roster for collaborative surfaces.
 *
 * The active tenant's team members feed BOTH the assignee picker and the
 * collaboration mention/comment-author roster, so "who can I assign / @-mention"
 * is one consistent list across the per-transaction workspace and the
 * standalone investigation workspace.
 *
 * Every read here reports its own failure. A roster that empties itself on a
 * failed fetch is not a smaller roster, it is a false one: the assignee picker
 * offers "Unassigned" alone, the mention list silently omits the colleague you
 * are trying to reach, and the collaboration transport concludes you belong to
 * no team and falls back to a channel that reaches nobody.
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "@authe/ui-core/session";
import { useOptionalStreamingClient } from "@authe/streaming";
import type { Member } from "@authe/fe-commons/teams";
import type { CollabUser } from "@authe/collaboration";
import { useTeamsApi } from "@/api/adapters";
import { useActiveTeam } from "./activeTeam";
import { useMyTeamsRead } from "./useMyTeams";

/** A roster read: the rows, plus whether they are an answer. */
export interface RosterRead<T> {
  rows: T;
  isLoading: boolean;
  /** The most recent read failed; `rows` may hold an earlier answer. */
  failed: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * The ACTIVE team's members (assignee options + mention roster), with the
 * read's own outcome.
 *
 * Scoped to `useActiveTeam`, not to the tenant. It used to build its own ref
 * from the tenant id — the backend's primary-team alias — so in a tenant with
 * more than one team the picker always listed the DEFAULT team's members and
 * there was no way to assign or @-mention anyone on the team you were actually
 * working in.
 */
export function useTeamMembersRead(): RosterRead<Member[]> {
  const api = useTeamsApi();
  const { team } = useActiveTeam();
  const { session } = useSession();
  const query = useQuery({
    queryKey: ["collaboration-roster", session?.tenant.customerId, session?.tenant.orgId,
      session?.tenant.tenantId, session?.user.id, team.id],
    queryFn: () => api.listMembers(team),
    enabled: Boolean(session?.user.id),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  return {
    rows: query.data ?? NO_MEMBERS,
    isLoading: query.isLoading,
    failed: query.isError,
    error: query.error,
    refetch: () => { if (session?.user.id) void query.refetch(); },
  };
}

/** The active team's members. Prefer {@link useTeamMembersRead} when writing. */
export function useTeamMembers(): Member[] {
  return useTeamMembersRead().rows;
}

/**
 * The teams the caller belongs to, as ids, with the read's own outcome.
 *
 * Both live surfaces need these: collaboration fans a room out over one channel
 * per team, and the transaction stream publishes team-targeted rows onto
 * `{org}-{tenant}-team#<id>-streamed-transactions`, so a subscriber that omits
 * its teams silently misses those rows. These ids also drive case permissions:
 * turning off streaming must not erase the caller's team memberships.
 */
export interface TeamIdsRead extends RosterRead<string[]> {
  /** A live streaming client is mounted, independently of memberships. */
  streaming: boolean;
  /** This build wires no memberships endpoint. */
  unavailable: boolean;
}

export function useTeamIdsRead(): TeamIdsRead {
  const streaming = useOptionalStreamingClient();
  const memberships = useMyTeamsRead();
  const rows = React.useMemo(() => memberships.teams.map((team) => team.id), [memberships.teams]);

  return {
    rows,
    isLoading: memberships.isLoading,
    failed: memberships.failed,
    error: memberships.error,
    streaming: Boolean(streaming),
    unavailable: memberships.unavailable,
    refetch: memberships.refetch,
  };
}

const NO_MEMBERS: Member[] = [];

/** The caller's team ids. Prefer {@link useTeamIdsRead} on a live surface. */
export function useTeamIds(): string[] {
  return useTeamIdsRead().rows;
}

export function toCollabUser(m: Member): CollabUser {
  return { id: m.id, name: m.name || m.email || m.id, avatarUrl: m.avatarUrl };
}

/**
 * The local user (`me`) + the mention/author roster (`me` first, then the
 * tenant's other members) for a collaboration surface. `me` is `null` until the
 * session resolves a user id.
 */
export function useCollabRoster(): {
  me: CollabUser | null;
  users: CollabUser[];
  /** The roster read failed, so `users` may be missing mentionable people. */
  failed: boolean;
} {
  const { session } = useSession();
  const { rows: members, failed } = useTeamMembersRead();
  return React.useMemo(() => {
    if (!session?.user.id) return { me: null, users: [], failed };
    const me: CollabUser = {
      id: session.user.id,
      name: session.user.name || session.user.email || session.user.id,
      avatarUrl: session.user.avatar ?? undefined,
    };
    const users: CollabUser[] = [me, ...members.filter((m) => m.id !== me.id).map(toCollabUser)];
    return { me, users, failed };
  }, [session, members, failed]);
}
